"use client";

import React, { useMemo } from "react";
import ErrorPage from "@/components/errors/ErrorPage";
import { getBugsnagErrorBoundary, isBugsnagEnabled } from "@/lib/bugsnag";

type Props = {
    children: React.ReactNode;
};

const BugsnagBoundary: React.FC<Props> = ({ children }) => {
    const ErrorBoundary = useMemo(() => getBugsnagErrorBoundary(React), []);

    if (!ErrorBoundary || !isBugsnagEnabled()) {
        return <>{children}</>;
    }

    const FallbackComponent = ({
        error,
        clearError,
    }: {
        error: Error;
        info: React.ErrorInfo;
        clearError: () => void;
    }) => (
        <div style={{ minHeight: "100vh" }}>
            <ErrorPage
                code={500}
                background="#eff4ff"
                accent="#0a49a7"
                title="ERROR 500"
                message={
                    <>
                        <div style={{ marginBottom: 8 }}>
                            Something Went Wrong
                        </div>
                        <div>
                            Our server hit a snag. Please try again in a few
                            moments.
                        </div>
                    </>
                }
                icon={null}
                primaryAction={{
                    kind: "retry",
                    label: "Try Again",
                    onRetry: clearError,
                }}
                secondaryAction={{
                    kind: "link",
                    label: "Back to Home",
                    href: "/",
                }}
            />
            {/* Surface stack locally for easier debugging while still reporting to Bugsnag */}
            {process.env.NODE_ENV === "development" && (
                <pre
                    style={{
                        padding: 16,
                        margin: 16,
                        background: "#fff5f5",
                        color: "#b91c1c",
                        borderRadius: 8,
                        overflowX: "auto",
                    }}
                >
                    {error.stack}
                </pre>
            )}
        </div>
    );

    return (
        <ErrorBoundary
            onError={(event) => {
                const href =
                    typeof window !== "undefined"
                        ? window.location.href
                        : undefined;

                event.addMetadata("react", {
                    url: href,
                });
            }}
            FallbackComponent={FallbackComponent}
        >
            {children}
        </ErrorBoundary>
    );
};

export default BugsnagBoundary;
